import { ColumnsType } from 'antd/es/table'
import { Avatar } from '../../../../components/Avatar/Avatar'
import { Sorter } from '../../../../components/Sorter/Sorter'
import { SortOrder } from 'antd/es/table/interface'
import { CellWithSubtitle } from '../../../../components/CellWithSubtitle/CellWithSubtitle'
import { Link } from '../../../../components/Link'
import { routes } from '../../../../router'
import { MY_COMMUNITY_KEYS, MY_COMMUNITY_USER_STATUSES } from '../../constants'
import { Dropdown } from '../../../../components/Dropdown'
import {
  Status,
  USER_STATUS_TYPES_TO_CLASSNAME
} from '../../../../components/Status'
import { StartChat } from '../../../../components/StartChat/StartChat'
import { fullFormatPhoneNumber } from '../../../../helper/common'
import { DescriptionCell } from '../../../../components/DescriptionCell/DescriptionCell'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import {
  CommunityUsersColumnsFunc,
  CommunityUsersFilters,
  CommunityUsersFiltersFunc,
  TCommunityUser
} from './types'
import { ROLES_NAMES, ROLES_OPTIONS_COMMUNITY } from '../../../../constants'
import { fetchHealthSystemsList } from '../../../HealthSystems/api'
import { fetchContractCategories } from '../../../../pages/CRUDUser/api'
import { getListOfDepartments, getListOfHospitals } from './api'
import { UserMenu } from '../../Hospitals/UsersTable/UserMenu'

export const OptionsMenu = ROLES_OPTIONS_COMMUNITY.map((role) => ({
  ...role,
  key: role.value
}))

export const columns = ({
  applyFilters,
  filters,
  onStartChat,
  canEditUser,
  community,
  selectedRole,
  onArchive
}: CommunityUsersColumnsFunc): ColumnsType<TCommunityUser> => {
  const _cols: ColumnsType<TCommunityUser> = [
    {
      dataIndex: 'first_name',
      key: 'first_name',
      className: 'avatar-cell',
      width: 44,
      title: null,
      render: (_, { first_name, avatar_logo, last_name }) => (
        <Avatar
          name={`${first_name[0]?.toUpperCase()}${last_name[0]?.toUpperCase()}`}
          src={avatar_logo?.file_preview || ''}
          alt="user image"
        />
      ),
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'name',
      key: 'name',
      width: 220,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TCommunityUser>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Name
        </Sorter>
      ),
      render: (_, { first_name, last_name, uuid }) =>
        canEditUser ? (
          <CellWithSubtitle
            title={
              <Link
                to={`${routes.createUserProfile}/${uuid}?fromCommunity=true`}
                state={{
                  redirectTab: MY_COMMUNITY_KEYS.communities
                }}
              >
                {first_name} {last_name}
              </Link>
            }
          />
        ) : (
          `${first_name} ${last_name}`
        ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 130,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TCommunityUser>
          dataIndex="status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Status
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<
          CommunityUsersFilters,
          CommunityUsersFiltersFunc,
          unknown
        >
          items={MY_COMMUNITY_USER_STATUSES}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="statuses"
          {...props}
        />
      ),
      render: (_, { status, uuid }) => (
        <div className="row gap-8 align-center">
          <Status.Default className={USER_STATUS_TYPES_TO_CLASSNAME[status]}>
            {status}
          </Status.Default>
          {status === 'Active' && (
            <StartChat onClick={() => onStartChat(uuid)} />
          )}
        </div>
      ),
      filterIcon: filters?.statuses?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'email',
      key: 'email',
      width: 240,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TCommunityUser>
          dataIndex="email"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Email
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'phone_number',
      key: 'phone_number',
      title: 'Phone number',
      width: 196,
      render: (_, { phone_number }) =>
        phone_number ? fullFormatPhoneNumber(phone_number) : '-',
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'contract_categories',
      key: 'contract_categories',
      width: 240,
      title: 'Contract categories',
      filterDropdown: (props) => (
        <Dropdown.Filter<
          CommunityUsersFilters,
          CommunityUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={fetchContractCategories}
          field="contract_categories"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { contract_categories }) => {
        return (
          <DescriptionCell
            content={contract_categories}
            text={contract_categories}
          />
        )
      },
      filterIcon: filters?.contract_categories?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'health_system',
      key: 'health_system',
      title: 'Health system',
      width: 240,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          CommunityUsersFilters,
          CommunityUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() => fetchHealthSystemsList(community, {})}
          field="health_systems"
          asyncSearch
          {...props}
        />
      ),
      filterIcon: filters?.health_systems?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      render: (_, { health_system }) => health_system,
      filterSearch: true,
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'hospital',
      key: 'hospital',
      title: 'Hospital',
      width: 240,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          CommunityUsersFilters,
          CommunityUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() => getListOfHospitals(selectedRole)}
          field="hospitals"
          asyncSearch
          {...props}
        />
      ),
      filterIcon: filters?.hospitals?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      render: (_, { hospital }) => hospital || '-',
      filterSearch: true,
      sorter: false,
      showSorterTooltip: false
    },
    {
      dataIndex: 'departments',
      key: 'departments',
      title: 'Departments',
      width: 240,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          CommunityUsersFilters,
          CommunityUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() => getListOfDepartments(selectedRole)}
          field="departments"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { departments }) => {
        return <DescriptionCell content={departments} text={departments} />
      },
      filterIcon: filters?.departments?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: false,
      showSorterTooltip: false
    }
  ]
  if (!selectedRole) {
    _cols.unshift({
      dataIndex: 'role',
      key: 'role',
      width: 180,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TCommunityUser>
          dataIndex="role"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Role
        </Sorter>
      ),
      render: (text) => ROLES_NAMES[text] ?? <>–</>,
      sorter: true,
      showSorterTooltip: false
    })
  }
  if (canEditUser) {
    _cols.push({
      dataIndex: 'action',
      key: 'action',
      width: 64,
      fixed: 'right',
      render: (_, row) => (
        <UserMenu
          user={{
            ...row,
            departments: row.departments?.split(',')
          }}
          onArchive={onArchive}
        />
      )
    })
  }
  return _cols
}
